import { GridColumn, GridRow, Icon, Popup } from "semantic-ui-react";

import { Editable, useCan } from "components";
import { useIsOverflowing, useTyrePositionName } from "hooks";
import { QuestionResultStatus } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/TyreTableRow/TyreTableRow.scss";
import { getFullTyreDescription, getTyreSize } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { isCarReadyOrQualityCheckDoneOrCanceled } from "utils/appointmentUtils";
import { getTyreSeasonIcon } from "utils/common";

import type { Appointment, VehicleTyre } from "models";

type TyreTableItemProps = {
  appointment: Appointment;
  vehicleTyre: VehicleTyre;
  readOnly?: boolean;
  onRequestReplacement: (tyre: VehicleTyre) => void;
};

export const TyreTableRow = ({ vehicleTyre, appointment, onRequestReplacement, readOnly }: TyreTableItemProps) => {
  const { ref: questionRef, isOverflowing: isQuestionOverflowing } = useIsOverflowing<HTMLSpanElement>();
  const { ref: sizeRef, isOverflowing: isSizeOverflowing } = useIsOverflowing<HTMLParagraphElement>();
  const { ref: typeRef, isOverflowing: isTypeOverflowing } = useIsOverflowing<HTMLParagraphElement>();
  const { ref: brandRef, isOverflowing: isBrandOverflowing } = useIsOverflowing<HTMLParagraphElement>();

  const tyrePositionName = useTyrePositionName(vehicleTyre.position);
  const appointmentLocation = useAppointmentLocation();

  const canAddTyreTeamReplacement = useCan("create", "tyreteam-replacement");
  const canAddOtherReplacement = useCan("create", "tyres-replacement");

  const canAddReplacement = !readOnly && (appointmentLocation?.is_tyre_team_enabled ? canAddTyreTeamReplacement : canAddOtherReplacement);
  const tyreSeasonIcon = getTyreSeasonIcon(vehicleTyre.tyre?.season);
  const size = getTyreSize(vehicleTyre.tyre);
  const description = getFullTyreDescription(vehicleTyre.tyre, appointmentLocation?.is_tyre_net_enabled);

  return (
    <GridRow className="TyreTableRow">
      <GridColumn width={2}>
        <div className="tyre-position-container">
          {canAddReplacement && !isCarReadyOrQualityCheckDoneOrCanceled(appointment.appointment_status_identifier) && (
            <div className="tyre-replacement-icon-container">
              <Icon className="plus circle" color="blue" onClick={() => onRequestReplacement(vehicleTyre)} />
            </div>
          )}
          <Popup hoverable disabled={!isQuestionOverflowing} content={tyrePositionName} trigger={<span ref={questionRef}>{tyrePositionName}</span>} />
        </div>
      </GridColumn>
      <GridColumn width={2}>
        <Popup
          hoverable
          disabled={!isSizeOverflowing}
          content={size}
          trigger={
            <div className="tyre-season-container">
              {tyreSeasonIcon && <Icon className={tyreSeasonIcon} />}
              <p ref={sizeRef}>{size}</p>
            </div>
          }
        />
      </GridColumn>
      <GridColumn width={4}>
        <Popup hoverable disabled={!isTypeOverflowing} content={description} trigger={<p ref={typeRef}>{description}</p>} />
      </GridColumn>
      <GridColumn width={1}>
        <Popup hoverable disabled={!isBrandOverflowing} content={vehicleTyre.tyre?.manufacturer} trigger={<p ref={brandRef}>{vehicleTyre.tyre?.manufacturer}</p>} />
      </GridColumn>
      <GridColumn width={1}>{vehicleTyre.profile ? `${vehicleTyre.profile} mm` : ""}</GridColumn>
      <GridColumn width={1}></GridColumn>
      <GridColumn width={3}>
        <Editable hoverable value={vehicleTyre.question_result_remark} disabled={true} />
      </GridColumn>
      <GridColumn width={2}>
        <div className="icons-container">
          {vehicleTyre.dbb_comment && (
            <Popup
              content={vehicleTyre.dbb_comment}
              trigger={
                <div className="dbb-icon-container">
                  <Icon className="tire regular full dbb-comment-icon" />
                </div>
              }
            />
          )}

          {vehicleTyre.question_result_status !== null && (
            <div className="question_result_status">
              <QuestionResultStatus status={vehicleTyre.question_result_status} />
            </div>
          )}
        </div>
      </GridColumn>
    </GridRow>
  );
};
